import React, { useState, useEffect } from 'react';
import { 
  Users, BookOpen, Upload, CheckCircle2, Clock, 
  FileText, Award, Video, Plus, Check, MessageSquare, ShieldCheck,
  Calendar, MapPin, ChevronRight, ChevronDown, Download, Send, 
  Sparkles, ExternalLink, RefreshCw, X, AlertCircle, Play, 
  Search, Filter, Laptop, Mic, Paperclip, BarChart3, Database, Eye,
  GraduationCap, HelpCircle, ArrowRight,
  Inbox, Bell, Radio, Megaphone, AlertTriangle, Mail
} from 'lucide-react';
import { 
  FACULTY_LECTURERS, 
  SEMESTER_COURSES, 
  getCourseByCode, 
  getCoursesByLecturerId,
  getFacultyById 
} from '../../data/semesterCourseData';
import { CourseEbookModal } from './CourseEbookModal';

export const FacultyDashboard = ({ authenticatedUser = null, onLogout = null, setActiveView = null }) => {
  // Determine initial faculty member: use authenticatedUser.facultyId or match by email/name, else default to Dr. Imoh Uford
  const initialFaculty = (() => {
    if (authenticatedUser) {
      if (authenticatedUser.facultyId) {
        const found = FACULTY_LECTURERS.find(f => f.id === authenticatedUser.facultyId);
        if (found) return found;
      }
      if (authenticatedUser.email) {
        const found = FACULTY_LECTURERS.find(f => f.email.toLowerCase() === authenticatedUser.email.toLowerCase());
        if (found) return found;
      }
      if (authenticatedUser.assignedCourses && authenticatedUser.assignedCourses.length > 0) {
        const found = FACULTY_LECTURERS.find(f => f.assignedCourses.some(c => authenticatedUser.assignedCourses.includes(c)));
        if (found) return found;
      }
    }
    return FACULTY_LECTURERS[0]; // Dr. Imoh Uford
  })();

  const [activeFacultyId, setActiveFacultyId] = useState(initialFaculty.id);
  const currentFaculty = FACULTY_LECTURERS.find(f => f.id === activeFacultyId) || FACULTY_LECTURERS[0];
  
  // Assigned courses for current faculty
  const facultyCourses = currentFaculty.assignedCourses.map(code => getCourseByCode(code));
  const [selectedCourseCode, setSelectedCourseCode] = useState(facultyCourses[0]?.code || "BUS 901");
  const selectedCourse = getCourseByCode(selectedCourseCode);

  // Active Workspace Sub-Tab
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'materials' | 'students' | 'assignments' | 'messaging' | 'research'

  // Sync selectedCourseCode when switching faculty
  useEffect(() => {
    if (currentFaculty && currentFaculty.assignedCourses.length > 0) {
      if (!currentFaculty.assignedCourses.includes(selectedCourseCode)) {
        setSelectedCourseCode(currentFaculty.assignedCourses[0]);
      }
    }
  }, [activeFacultyId]);

  // Synchronize if authenticatedUser prop changes
  useEffect(() => {
    if (authenticatedUser?.facultyId) {
      setActiveFacultyId(authenticatedUser.facultyId);
    }
  }, [authenticatedUser]);

  // Dynamic In-Memory Course Materials State (seeded from course master)
  const [courseMaterials, setCourseMaterials] = useState(() => {
    const store = {};
    SEMESTER_COURSES.forEach(c => {
      store[c.code] = [...(c.materials || [])];
    });
    return store;
  });

  // Dynamic In-Memory Assignments State
  const [courseAssignments, setCourseAssignments] = useState(() => {
    const store = {};
    SEMESTER_COURSES.forEach(c => {
      store[c.code] = [...(c.assignments || [])];
    });
    return store;
  });

  // Dynamic In-Memory Student Submissions State
  const [courseSubmissions, setCourseSubmissions] = useState(() => {
    const store = {};
    SEMESTER_COURSES.forEach(c => {
      store[c.code] = (c.enrolledStudents || []).map((s, idx) => ({
        id: `sub-${c.code}-${idx}`,
        matric: s.matric,
        studentName: s.name,
        programme: c.programmeName,
        taskTitle: c.assignments?.[0]?.title || "Continuous Assessment Paper",
        submittedDate: "18 Oct 2026, 14:32",
        status: s.submissionStatus || "Submitted",
        grade: s.grade || "Pending",
        feedback: s.grade && s.grade !== "Pending" ? "Methodology approved. Clear theoretical contribution." : "",
        avatar: s.avatar
      }));
    });
    return store;
  });

  // Dynamic In-Memory Faculty Working Papers State
  const [facultyResearchPapers, setFacultyResearchPapers] = useState(() => {
    const store = {};
    FACULTY_LECTURERS.forEach(fac => {
      store[fac.id] = [...(fac.workingPapers || [])];
    });
    return store;
  });

  // Modals & Banners State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedSubmissionToGrade, setSelectedSubmissionToGrade] = useState(null);
  const [inputGradeScore, setInputGradeScore] = useState('90');
  const [inputGradeFeedback, setInputGradeFeedback] = useState('Insightful empirical justification. Ensure the methodology chapter reports robustness checks.');
  const [uploadSuccessAlert, setUploadSuccessAlert] = useState(null);
  const [actionSuccessAlert, setActionSuccessAlert] = useState(null);
  const [showLiveStudioModal, setShowLiveStudioModal] = useState(false);
  const [lecturerSwitcherOpen, setLecturerSwitcherOpen] = useState(false);
  const [showEbookModal, setShowEbookModal] = useState(false);
  const [ebookCourseCode, setEbookCourseCode] = useState("BUS 911");

  const handleOpenEbook = (code = selectedCourse.code) => {
    setEbookCourseCode(code || "BUS 911");
    setShowEbookModal(true);
  };

  // Class Broadcast & Blinking Red Attention Alert State
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastPriority, setBroadcastPriority] = useState('urgent'); // 'urgent' | 'normal'
  const [sendBlinkingAlert, setSendBlinkingAlert] = useState(true);
  const [broadcastChannels, setBroadcastChannels] = useState({
    inbox: true,
    whatsapp: true,
    email: true
  });
  const [broadcastHistory, setBroadcastHistory] = useState(() => {
    try {
      const stored = localStorage.getItem('aksu_class_broadcasts');
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return [
      {
        id: 'bcast-init-1',
        courseCode: 'BUS 901',
        courseTitle: 'Advanced Research Methodology & Seminar',
        lecturerName: 'Dr. Imoh Uford',
        lecturerRank: 'Senior Lecturer & Pioneer Director',
        lecturerAvatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=300&auto=format&fit=crop',
        subject: 'Urgent: Rescheduled Saturday Executive Seminar & Zoom Link',
        message: "Distinguished Doctoral Scholars of BUS 901,\\n\\nPlease be informed that our upcoming Saturday Executive Seminar has been synchronized for hybrid attendance. Pre-reading on Saunders' Research Onion (Chapter 4) is uploaded to your course repository. Please review before session.",
        priority: 'urgent',
        sendBlinkingAlert: true,
        channels: ['inbox', 'whatsapp', 'email'],
        timestamp: 'Today, 09:15 AM'
      }
    ];
  });
  const [lecturerInboxBlinking, setLecturerInboxBlinking] = useState(false);

  const handleOpenBroadcastModal = (mode = 'urgent') => {
    if (mode === 'urgent') {
      setBroadcastSubject(`Urgent Academic Directive: ${selectedCourse.code} Hybrid Seminar & CA1 Matrix`);
      setBroadcastMessage(`Distinguished Scholars of ${selectedCourse.code},\n\nPlease note an urgent update regarding our module schedule and continuous assessment deliverables. The syndicate seminar will commence promptly with hybrid video access enabled. Pre-reading frameworks have been uploaded to your CDN repository.\n\nAll enrolled scholars are required to acknowledge receipt in their portal inbox immediately.\n\n— ${currentFaculty.name}, ${currentFaculty.rank}`);
      setBroadcastPriority('urgent');
      setSendBlinkingAlert(true);
    } else {
      setBroadcastSubject(`Notice to ${selectedCourse.code} Scholars: Supplementary Courseware`);
      setBroadcastMessage(`Dear Enrolled Scholars,\n\nSupplementary reading resources and lecture slides for ${selectedCourse.code} have been updated in your course repository.\n\nBest regards,\n${currentFaculty.name}`);
      setBroadcastPriority('normal');
      setSendBlinkingAlert(false);
    }
    setShowBroadcastModal(true);
  };

  const handleSendBroadcast = (e) => {
    e?.preventDefault();
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) return;

    const newBroadcast = {
      id: `bcast-${Date.now()}`,
      courseCode: selectedCourse.code,
      courseTitle: selectedCourse.title || selectedCourse.name,
      lecturerId: currentFaculty.id,
      lecturerName: currentFaculty.name,
      lecturerRank: currentFaculty.rank,
      lecturerAvatar: currentFaculty.avatar,
      subject: broadcastSubject,
      message: broadcastMessage,
      priority: broadcastPriority,
      sendBlinkingAlert: sendBlinkingAlert,
      channels: Object.keys(broadcastChannels).filter(k => broadcastChannels[k]),
      timestamp: 'Just now'
    };

    const updated = [newBroadcast, ...broadcastHistory];
    setBroadcastHistory(updated);
    try {
      localStorage.setItem('aksu_class_broadcasts', JSON.stringify(updated));
    } catch (err) {}

    // If blinking alert option is toggled, set active urgent alert in localStorage and dispatch event
    if (sendBlinkingAlert) {
      const urgentAlertPayload = {
        id: `alert-${Date.now()}`,
        broadcastId: newBroadcast.id,
        courseCode: selectedCourse.code,
        courseTitle: selectedCourse.title || selectedCourse.name,
        senderName: currentFaculty.name,
        senderRank: currentFaculty.rank,
        senderAvatar: currentFaculty.avatar,
        subject: broadcastSubject,
        message: broadcastMessage,
        priority: broadcastPriority,
        isBlinking: true,
        acknowledged: false,
        timestamp: 'Just now'
      };
      try {
        localStorage.setItem('aksu_urgent_inbox_alert', JSON.stringify(urgentAlertPayload));
      } catch (err) {}

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aksu_urgent_alert_change', { detail: urgentAlertPayload }));
        window.dispatchEvent(new CustomEvent('aksu_broadcast_change', { detail: updated }));
      }
    }

    setShowBroadcastModal(false);
    setActionSuccessAlert(
      sendBlinkingAlert 
        ? `Class broadcast dispatched to all ${selectedCourse.code} scholars! 🚨 Blinking Red Inbox Alert is now ACTIVE on student portals.`
        : `Class broadcast dispatched to all ${selectedCourse.code} scholars.`
    );
    setTimeout(() => setActionSuccessAlert(null), 7000);
  };

  const handleToggleLecturerBlinking = () => {
    setLecturerInboxBlinking(prev => !prev);
    setActionSuccessAlert(!lecturerInboxBlinking 
      ? "🚨 Lecturer Incoming Inbox Blinking Alert activated! (Simulating incoming urgent scholar inquiry)"
      : "Lecturer Inbox Alert cleared."
    );
    setTimeout(() => setActionSuccessAlert(null), 5000);
  };

  // Upload Form Inputs
  const [newMaterialTitle, setNewMaterialTitle] = useState('');
  const [newMaterialCategory, setNewMaterialCategory] = useState('Slide Deck');
  const [newMaterialTopic, setNewMaterialTopic] = useState('Module 1');
  const [selectedFilterCategory, setSelectedFilterCategory] = useState('All');

  // New Assignment Form Inputs
  const [newAssignTitle, setNewAssignTitle] = useState('');
  const [newAssignBrief, setNewAssignBrief] = useState('');
  const [newAssignDueDate, setNewAssignDueDate] = useState('20th November 2026');
  const [newAssignWeight, setNewAssignWeight] = useState('30%');

  // New Research Paper Inputs
  const [newResearchTitle, setNewResearchTitle] = useState('');
  const [newResearchStatus, setNewResearchStatus] = useState('Under Review');
  const [newResearchGrant, setNewResearchGrant] = useState('TETFUND National Research Fund');

  // Messaging State
  const [conversationHistory, setConversationHistory] = useState([
    {
      id: 'msg-fac-1',
      sender: 'student',
      senderName: 'Emem Udoh',
      matric: 'AKSU/BS/2026/DBA/012',
      time: 'Yesterday, 14:15',
      subject: `Inquiry on ${selectedCourse.code} Continuous Assessment`,
      text: `Good day Professor. Regarding the continuous assessment deliverable, should we report empirical results using the standard APA 7th edition regression tables, or does the Business School prefer the Harvard Business Review executive summary format?`,
      status: 'Read'
    },
    {
      id: 'msg-fac-2',
      sender: 'faculty',
      senderName: currentFaculty.name,
      time: 'Yesterday, 16:40',
      subject: `RE: Inquiry on ${selectedCourse.code} Continuous Assessment`,
      text: `Dear Scholar Udoh,\n\nFor continuous assessment in this doctoral module, please report the full statistical estimation tables in APA 7th format in your appendix, but precede them with an executive 2-page decision brief for C-suite readability. We will review sample matrices during our upcoming Saturday seminar.\n\nWarm regards,\n${currentFaculty.name}`,
      status: 'Sent'
    }
  ]);
  const [replyMessageText, setReplyMessageText] = useState('');

  // Handle New Material Upload
  const handleAddMaterial = (e) => {
    e.preventDefault();
    if (!newMaterialTitle.trim()) return;

    const newMat = {
      id: `mat-${Date.now()}`,
      title: newMaterialTitle,
      type: newMaterialCategory,
      size: "4.8 MB",
      date: "Just Now",
      file: `${selectedCourse.code.replace(/\s+/g, '')}_${newMaterialTitle.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
      status: "Verified & Live"
    };

    setCourseMaterials(prev => ({
      ...prev,
      [selectedCourse.code]: [newMat, ...(prev[selectedCourse.code] || [])]
    }));

    setUploadSuccessAlert({
      title: newMaterialTitle,
      course: selectedCourse.code,
      type: newMaterialCategory
    });
    setNewMaterialTitle('');
    setShowUploadModal(false);

    setTimeout(() => setUploadSuccessAlert(null), 5000);
  };

  // Handle New Assignment Creation
  const handleAddAssignment = (e) => {
    e.preventDefault();
    if (!newAssignTitle.trim()) return;

    const newAsg = {
      id: `asg-${Date.now()}`,
      title: newAssignTitle,
      brief: newAssignBrief || "Complete continuous assessment paper conforming to institutional research benchmarks.",
      dueDate: newAssignDueDate,
      weight: `${newAssignWeight} Continuous Assessment`,
      points: 30,
      submissionsCount: selectedCourse.enrolledStudents?.length || 5,
      status: "Active / Published"
    };

    setCourseAssignments(prev => ({
      ...prev,
      [selectedCourse.code]: [newAsg, ...(prev[selectedCourse.code] || [])]
    }));

    setActionSuccessAlert(`Assignment "${newAssignTitle}" published to ${selectedCourse.code} enrolled scholars.`);
    setNewAssignTitle('');
    setNewAssignBrief('');
    setShowAssignmentModal(false);

    setTimeout(() => setActionSuccessAlert(null), 5000);
  };

  // Handle Grading a Student Submission
  const handleOpenGradeModal = (submission) => {
    setSelectedSubmissionToGrade(submission);
    setInputGradeScore(submission.grade !== "Pending" ? submission.grade.replace('/100', '') : '92');
    setInputGradeFeedback(submission.feedback || 'Rigorous theoretical alignment with verifiable empirical evidence.');
    setShowGradeModal(true);
  };

  const handleSaveGrade = (e) => {
    e.preventDefault();
    if (!selectedSubmissionToGrade) return;

    setCourseSubmissions(prev => ({
      ...prev,
      [selectedCourse.code]: (prev[selectedCourse.code] || []).map(sub => {
        if (sub.id === selectedSubmissionToGrade.id || sub.matric === selectedSubmissionToGrade.matric) {
          return {
            ...sub,
            status: "Graded",
            grade: `${inputGradeScore}/100`,
            feedback: inputGradeFeedback
          };
        }
        return sub;
      })
    }));

    setShowGradeModal(false);
    setActionSuccessAlert(`Grade ${inputGradeScore}/100 recorded for ${selectedSubmissionToGrade.studentName}. Scholar notified.`);
    setTimeout(() => setActionSuccessAlert(null), 5000);
  };

  // Handle Adding a Research Paper
  const handleAddResearchPaper = (e) => {
    e.preventDefault();
    if (!newResearchTitle.trim()) return;

    const newPaper = {
      id: `wp-${Date.now()}`,
      title: newResearchTitle,
      status: newResearchStatus,
      date: "October 2026",
      grantSupport: newResearchGrant
    };

    setFacultyResearchPapers(prev => ({
      ...prev,
      [currentFaculty.id]: [newPaper, ...(prev[currentFaculty.id] || [])]
    }));

    setShowResearchModal(false);
    setNewResearchTitle('');
    setActionSuccessAlert(`Research paper "${newResearchTitle}" registered to your academic portfolio.`);
    setTimeout(() => setActionSuccessAlert(null), 5000);
  };

  // Handle Sending Messaging Reply
  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyMessageText.trim()) return;

    const newReply = {
      id: `msg-fac-${Date.now()}`,
      sender: 'faculty',
      senderName: currentFaculty.name,
      time: 'Just now',
      subject: `RE: ${conversationHistory[0]?.subject || 'Academic Consultation'}`,
      text: replyMessageText,
      status: 'Sent'
    };

    setConversationHistory(prev => [...prev, newReply]);
    setReplyMessageText('');
    setActionSuccessAlert("Response transmitted to scholar's LMS messaging portal.");
    setTimeout(() => setActionSuccessAlert(null), 4000);
  };

  const currentCourseMaterials = courseMaterials[selectedCourse.code] || [];
  const currentCourseAssignments = courseAssignments[selectedCourse.code] || [];
  const currentCourseSubmissions = courseSubmissions[selectedCourse.code] || [];
  const currentResearchPapers = facultyResearchPapers[currentFaculty.id] || [];

  const filteredMaterials = selectedFilterCategory === 'All'
    ? currentCourseMaterials
    : currentCourseMaterials.filter(m => m.type.toLowerCase().includes(selectedFilterCategory.toLowerCase()));

  return (
    <div className="space-y-6">
      
      {/* ========================================================================= */}
      {/* 1. TOP INTERACTIVE LECTURER WORKSPACE SELECTOR & FAST-PASS                 */}
      {/* ========================================================================= */}
      <div className="bg-[#030F1C] text-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 border-2 border-[#7A470E] shadow-xl">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#7A470E] to-[#0B2644] border border-amber-400/60 flex items-center justify-center text-amber-300 shadow-md shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-mono uppercase tracking-wider text-amber-400 font-bold">
                  Tier 2 Scoped Authority · Course Lecturer Workspace
                </span>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
              <h2 className="text-base sm:text-lg font-black font-sc text-white">
                Assigned Academic Chambers & Course Delivery Console
              </h2>
            </div>
          </div>

          {/* Quick Action Controls: Class Broadcast & Blinking Inbox Alert */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            
            {/* Quick Broadcast Button */}
            <button
              onClick={() => handleOpenBroadcastModal('urgent')}
              className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 border-2 border-red-400 text-white font-sc font-bold text-xs flex items-center space-x-1.5 shadow-md cursor-pointer transition-all animate-pulse"
              title="Broadcast message to class and trigger blinking red inbox alert"
            >
              <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
              <span>Broadcast Alert</span>
            </button>

            {/* Lecturer Inbox Status Pill */}
            <button
              onClick={handleToggleLecturerBlinking}
              className={`px-3 py-2 rounded-xl font-sc font-bold text-xs flex items-center space-x-1.5 border transition-all cursor-pointer ${
                lecturerInboxBlinking
                  ? 'bg-red-600 text-white border-red-300 animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.9)]'
                  : 'bg-[#0B2644] text-slate-200 hover:text-white border-slate-700'
              }`}
              title="Toggle Lecturer Inbox Attention Alert"
            >
              <Inbox className={`w-3.5 h-3.5 ${lecturerInboxBlinking ? 'text-white' : 'text-amber-300'}`} />
              <span className="hidden sm:inline">Lecturer Inbox</span>
              {lecturerInboxBlinking ? (
                <span className="px-1.5 py-0.2 rounded-full bg-white text-red-700 font-mono text-[9px] font-black">
                  WAITING
                </span>
              ) : (
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
              )}
            </button>

            {/* Quick Lecturer Workspace Switcher Dropdown */}
            <div className="relative">
            <button
              onClick={() => setLecturerSwitcherOpen(!lecturerSwitcherOpen)}
              className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#0B2644] to-[#06182B] hover:from-[#7A470E] hover:to-[#5C3306] border-2 border-amber-400/60 text-white font-sc font-bold text-xs flex items-center justify-between space-x-3 shadow-md cursor-pointer transition-all"
            >
              <div className="flex items-center space-x-2.5 text-left">
                <img 
                  src={currentFaculty.avatar} 
                  alt={currentFaculty.name} 
                  className="w-6 h-6 rounded-full object-cover border border-amber-300"
                />
                <div>
                  <div className="text-xs font-bold text-amber-200">{currentFaculty.name}</div>
                  <div className="text-[10px] text-slate-300 font-mono">{currentFaculty.assignedCourses.join(' · ')}</div>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-amber-300 transition-transform ${lecturerSwitcherOpen ? 'rotate-180' : ''}`} />
            </button>

            {lecturerSwitcherOpen && (
              <div className="absolute right-0 mt-2 w-full md:w-96 bg-white rounded-2xl shadow-2xl border-2 border-slate-300 py-2 z-50 text-slate-900 animate-in fade-in slide-in-from-top-2 duration-150 max-h-[80vh] overflow-y-auto">
                <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc">
                    Select Course Lecturer Workspace (11 Chairs)
                  </span>
                  <span className="text-[10px] font-mono text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full font-bold">
                    All Active
                  </span>
                </div>

                <div className="divide-y divide-slate-100">
                  {FACULTY_LECTURERS.map((fac) => {
                    const isSelected = fac.id === currentFaculty.id;
                    return (
                      <button
                        key={fac.id}
                        onClick={() => {
                          setActiveFacultyId(fac.id);
                          setLecturerSwitcherOpen(false);
                        }}
                        className={`w-full text-left p-3 flex items-center justify-between transition-colors cursor-pointer ${
                          isSelected ? 'bg-amber-50/90 border-l-4 border-[#7A470E]' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <img 
                            src={fac.avatar} 
                            alt={fac.name} 
                            className="w-9 h-9 rounded-xl object-cover border border-[#7A470E]/50 shrink-0" 
                          />
                          <div>
                            <div className="text-xs font-black text-[#0B2644] font-sc">
                              {fac.name}
                            </div>
                            <div className="text-[11px] text-slate-500 font-sans line-clamp-1">
                              {fac.rank}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1">
                              {fac.assignedCourses.map(c => (
                                <span key={c} className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-[#0B2644] text-amber-300 font-mono">
                                  {c}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-[#7A470E] shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. DEDICATED FACULTY PROFILE BANNER                                       */}
      {/* ========================================================================= */}
      <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] text-white rounded-3xl p-5 sm:p-8 border-2 border-[#7A470E] shadow-xl">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          
          <div className="flex items-start sm:items-center space-x-4">
            <div className="relative shrink-0">
              <img 
                src={currentFaculty.avatar} 
                alt={currentFaculty.name} 
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border-2 border-amber-400 shadow-xl"
              />
              <span className="absolute -bottom-1 -right-1 bg-emerald-500 w-4 h-4 rounded-full border-2 border-slate-900" title="Online & Active Desk" />
            </div>
            
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-amber-300 bg-amber-500/10 px-2.5 py-0.5 rounded-full border border-amber-400/40 font-sc">
                  {currentFaculty.title}
                </span>
                <span className="text-xs text-slate-400 font-mono hidden sm:inline">|</span>
                <span className="text-xs text-slate-300 font-sans font-medium">
                  {currentFaculty.office}
                </span>
              </div>
              
              <h1 className="text-xl sm:text-3xl font-black font-sc text-white tracking-wide">
                {currentFaculty.name}
              </h1>
              
              <div className="text-xs text-slate-300 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{currentFaculty.qualifications}</span>
                <span className="text-amber-400">•</span>
                <span>{currentFaculty.department}</span>
                <span className="text-amber-400">•</span>
                <span className="text-amber-300/90 font-mono">Consultation: {currentFaculty.consultationHours}</span>
              </div>
            </div>
          </div>

          {/* Assigned Course Switcher Tabs */}
          <div className="w-full lg:w-auto bg-[#030F1C]/90 p-2 rounded-2xl border border-slate-700/80">
            <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mb-1.5 px-2 font-bold">
              Assigned Course Desks ({currentFaculty.assignedCourses.length}):
            </div>
            <div className="flex flex-wrap gap-1.5">
              {currentFaculty.assignedCourses.map(code => {
                const courseObj = getCourseByCode(code);
                const isSelected = selectedCourseCode === code;
                return (
                  <button
                    key={code}
                    onClick={() => setSelectedCourseCode(code)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-black font-sc transition-all cursor-pointer flex items-center space-x-2 ${
                      isSelected
                        ? 'bg-[#7A470E] text-white shadow-md border border-amber-300'
                        : 'bg-[#0B2644] text-slate-300 hover:text-white hover:bg-[#133562]'
                    }`}
                  >
                    <span>{code}</span>
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-black/30 text-amber-200 font-mono">
                      {courseObj.programmeId?.toUpperCase()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

        </div>

        {/* Action Alerts */}
        {uploadSuccessAlert && (
          <div className="mt-4 p-3.5 rounded-2xl bg-emerald-950/90 border-2 border-emerald-400/60 text-emerald-200 text-xs flex items-center justify-between animate-in fade-in duration-200">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>
                <strong>{uploadSuccessAlert.title}</strong> has been uploaded and published to <strong>{uploadSuccessAlert.course}</strong> repository!
              </span>
            </div>
            <button onClick={() => setUploadSuccessAlert(null)} className="text-emerald-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {actionSuccessAlert && (
          <div className="mt-4 p-3.5 rounded-2xl bg-blue-950/90 border-2 border-cyan-400/60 text-cyan-200 text-xs flex items-center justify-between animate-in fade-in duration-200">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-cyan-300 shrink-0" />
              <span>{actionSuccessAlert}</span>
            </div>
            <button onClick={() => setActionSuccessAlert(null)} className="text-cyan-300 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 3. DEDICATED WORKSPACE NAVIGATION TABS                                     */}
      {/* ========================================================================= */}
      <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-2 overflow-x-auto tabs-scroll-container">
        <div className="flex items-center space-x-1.5 min-w-max text-xs font-sc font-bold">
          {[
            { id: 'overview', label: 'Course Overview & Timetable', icon: BookOpen },
            { id: 'materials', label: `Course Materials (${currentCourseMaterials.length})`, icon: Upload, badge: 'Live CDN' },
            { id: 'students', label: `Assigned Scholars (${selectedCourse.enrolledStudents?.length || 5})`, icon: Users },
            { id: 'assignments', label: `Assignments & Grading (${currentCourseSubmissions.length})`, icon: Award, badge: 'Desk' },
            { id: 'messaging', label: 'Student Communication Desk', icon: MessageSquare, badge: 'Active' },
            { id: 'research', label: `Research & Publications (${currentResearchPapers.length})`, icon: FileText }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 rounded-xl transition-all cursor-pointer flex items-center space-x-2 whitespace-nowrap ${
                  isActive
                    ? 'bg-[#0B2644] text-white shadow-md font-black border border-[#7A470E]'
                    : 'text-slate-600 hover:text-[#0B2644] hover:bg-slate-100'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-amber-300' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={`px-1.5 py-0.2 rounded text-[9px] font-mono font-black ${
                    isActive ? 'bg-amber-400 text-slate-900' : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 4. TAB CONTENTS                                                           */}
      {/* ========================================================================= */}

      {/* TAB 1: OVERVIEW & TIMETABLE */}
      {activeTab === 'overview' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          {/* Quick Metrics Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border-2 border-slate-200 shadow-sm space-y-1">
              <span className="text-[11px] font-bold text-slate-500 uppercase font-sc">Enrolled Scholars</span>
              <div className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644]">
                {selectedCourse.enrolledStudents?.length || 5}
              </div>
              <span className="text-[10px] text-emerald-600 font-mono font-bold">● 100% Verified Bursary Clearance</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border-2 border-slate-200 shadow-sm space-y-1">
              <span className="text-[11px] font-bold text-slate-500 uppercase font-sc">Published Materials</span>
              <div className="text-2xl sm:text-3xl font-black font-sc text-[#7A470E]">
                {currentCourseMaterials.length} Items
              </div>
              <span className="text-[10px] text-slate-500 font-mono">Slides, Cases, Datasets & Video</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border-2 border-slate-200 shadow-sm space-y-1">
              <span className="text-[11px] font-bold text-slate-500 uppercase font-sc">CA Submission Rate</span>
              <div className="text-2xl sm:text-3xl font-black font-sc text-[#0B2644]">
                94%
              </div>
              <span className="text-[10px] text-emerald-600 font-mono font-bold">● Continuous Assessment Active</span>
            </div>

            <div className="bg-white p-5 rounded-2xl border-2 border-slate-200 shadow-sm space-y-1">
              <span className="text-[11px] font-bold text-slate-500 uppercase font-sc">Cohort Avg Grade</span>
              <div className="text-2xl sm:text-3xl font-black font-sc text-emerald-700">
                89.2%
              </div>
              <span className="text-[10px] text-slate-500 font-mono">Executive Distinction Track</span>
            </div>
          </div>

          {/* Next Scheduled Seminar & Hybrid Studio Block */}
          <div className="bg-gradient-to-r from-[#06182B] via-[#0B2644] to-[#06182B] text-white p-6 sm:p-8 rounded-3xl border-2 border-[#7A470E] shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center space-x-2 text-xs font-mono font-bold text-amber-300 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-400/40">
                <Calendar className="w-3.5 h-3.5" />
                <span>Next Scheduled Executive Seminar: {selectedCourse.schedule}</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black font-sc text-white">
                {selectedCourse.code}: {selectedCourse.title}
              </h3>
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                <div className="flex items-center space-x-1">
                  <MapPin className="w-3.5 h-3.5 text-amber-300" />
                  <span>{selectedCourse.venue}</span>
                </div>
                <span>•</span>
                <span>{selectedCourse.programmeName}</span>
                <span>•</span>
                <span className="font-mono text-amber-200">{selectedCourse.credits} Credit Units</span>
              </div>
            </div>

            <button
              onClick={() => setShowLiveStudioModal(true)}
              className="px-5 py-3 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#B87A28] hover:from-[#5C3306] hover:to-[#7A470E] text-white font-sc font-black text-xs flex items-center space-x-2 shadow-lg cursor-pointer transition-all hover:scale-[1.02] shrink-0"
            >
              <Video className="w-4 h-4" />
              <span>Launch Hybrid Broadcast Studio</span>
            </button>
          </div>

          {/* Course Synopsis & Learning Outcomes */}
          {selectedCourse.code === 'BUS 911' || selectedCourse.code === 'DAF 911' || currentFaculty.id === 'fac-daniel' ? (
            /* ========================================================================= */
            /* DEDICATED REPURPOSED WORKSPACE FOR DR. EKERE JACOB DANIEL (BUS/DAF 911)   */
            /* ========================================================================= */
            <div className="space-y-6">
              
              {/* Luxury Official Course Outline & eBook Handbook Banner */}
              <div className="bg-gradient-to-r from-[#030F1C] via-[#06182B] to-[#0B2644] rounded-3xl p-6 sm:p-8 border-2 border-amber-400 text-white shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#7A470E]/20 rounded-full blur-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                  
                  {/* Left: Dual Official Logos + Lecturer Photo */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex items-center space-x-2 shrink-0">
                      {/* Akwa Ibom State University Crest */}
                      <div className="w-16 h-16 rounded-2xl bg-white/10 p-1.5 border-2 border-amber-400/80 shadow-lg flex items-center justify-center">
                        <img 
                          src="/aksu-university-crest-official.jpg" 
                          alt="AKSU Official Crest" 
                          className="w-full h-full object-contain" 
                        />
                      </div>
                      {/* AKSU Business School Shield */}
                      <div className="w-16 h-16 rounded-2xl bg-white/10 p-1.5 border-2 border-amber-400/80 shadow-lg flex items-center justify-center">
                        <img 
                          src="/aksu-business-school-shield-official.png" 
                          alt="AKSU Business School Shield" 
                          className="w-full h-full object-contain" 
                        />
                      </div>
                      {/* Dr. Ekere Jacob Daniel Photo */}
                      <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-lg shrink-0">
                        <img 
                          src="/dr-daniel-ekere.png" 
                          alt="Dr. Ekere Jacob Daniel" 
                          className="w-full h-full object-cover object-top" 
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-amber-300 bg-amber-500/20 px-2.5 py-0.5 rounded-full border border-amber-400/40">
                          First Semester 2025/2026 · DBA & DAF
                        </span>
                        <span className="text-[10px] font-mono text-emerald-400 flex items-center">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Official Course Outline Ratified
                        </span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-black font-sc text-white leading-tight">
                        BUS/DAF 911: Advanced Quantitative Analysis and Econometrics
                      </h3>
                      <p className="text-xs text-slate-300 font-sans">
                        Course Facilitator: <strong className="text-amber-200">Dr. Ekere Jacob Daniel</strong> · 3 Credit Units · Alternate Saturdays 12:30 – 15:30
                      </p>
                    </div>
                  </div>

                  {/* Right: 3 Luxury Action Triggers */}
                  <div className="flex flex-wrap items-center gap-2.5 shrink-0 w-full lg:w-auto">
                    <button
                      onClick={() => handleOpenEbook(selectedCourse.code)}
                      className="flex-1 sm:flex-initial px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#B87A28] hover:from-[#B87A28] hover:to-[#D4AF37] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg transition-all hover:scale-105 cursor-pointer"
                      title="Open Interactive HTML Course Material & eBook"
                    >
                      <BookOpen className="w-4 h-4 text-amber-200" />
                      <span>Launch Interactive Course eBook</span>
                    </button>

                    <button
                      onClick={() => handleOpenEbook(selectedCourse.code)}
                      className="px-3.5 py-2.5 rounded-xl bg-[#06182B] hover:bg-[#0B2644] text-amber-200 font-sc font-bold text-xs border border-amber-400/50 flex items-center justify-center space-x-1.5 transition-colors cursor-pointer"
                      title="Download Offline HTML & PDF Package"
                    >
                      <Download className="w-3.5 h-3.5 text-amber-300" />
                      <span>Download HTML / PDF</span>
                    </button>
                  </div>

                </div>
              </div>

              {/* Course Overview & Learning Outcomes */}
              <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#7A470E] font-sc block mb-1">
                    Official Course Description
                  </span>
                  <h3 className="text-xl font-black font-sc text-[#0B2644]">
                    Advanced Quantitative Analysis and Econometrics
                  </h3>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed font-sans">
                    {selectedCourse.overview}
                  </p>
                </div>

                <div className="border-t border-slate-200 pt-5">
                  <h4 className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc mb-3">
                    Core Doctoral Learning Outcomes:
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedCourse.learningOutcomes?.map((outcome, idx) => (
                      <div key={idx} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex items-start space-x-2.5">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span className="text-xs text-slate-800 font-sans leading-relaxed">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 13-Week Doctoral Syllabus Matrix */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#7A470E] font-sc block">
                        Senate Approved Delivery Matrix
                      </span>
                      <h4 className="text-lg font-black font-sc text-[#0B2644]">
                        Full 13-Week Detailed Course Outline
                      </h4>
                    </div>
                    <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-[#0B2644] text-amber-200 self-start sm:self-auto">
                      Weeks 1 to 13 · 180 Mins / Session
                    </span>
                  </div>

                  <div className="space-y-3">
                    {selectedCourse.modules?.map((m, idx) => (
                      <div key={idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#7A470E] transition-all space-y-2.5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center space-x-2.5">
                            <span className="px-2.5 py-0.5 rounded-lg bg-[#0B2644] text-amber-200 font-mono font-bold text-xs">
                              {m.number || `Week ${idx + 1}`}
                            </span>
                            <h5 className="font-sc font-bold text-sm sm:text-base text-slate-900">
                              {m.title}
                            </h5>
                          </div>
                          <span className="text-[11px] font-mono text-slate-500">
                            {m.duration}
                          </span>
                        </div>

                        {m.majorTopic && (
                          <div className="text-xs font-semibold text-[#0B2644]">
                            Major Topic: <span className="font-normal text-slate-700">{m.majorTopic}</span>
                          </div>
                        )}

                        {m.minorTopics && m.minorTopics.length > 0 && (
                          <div className="bg-white p-3 rounded-xl border border-slate-200">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-bold block mb-1">
                              Subtopics / Laboratory Exercises:
                            </span>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-1 text-xs text-slate-600">
                              {m.minorTopics.map((sub, sIdx) => (
                                <li key={sIdx} className="flex items-start space-x-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#7A470E] shrink-0 mt-1.5" />
                                  <span>{sub}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {m.readings && m.readings.length > 0 && (
                          <div className="p-2 rounded-xl bg-amber-50/70 border-l-3 border-[#7A470E] text-[11px] text-[#7A470E] font-mono">
                            <strong className="text-[#0B2644] font-sans">Prescribed Readings: </strong>
                            {m.readings.join('; ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Prescribed Reference Textbooks with Live Hyperlinks */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#7A470E] font-sc block">
                        Prescribed Course Bibliography
                      </span>
                      <h4 className="text-lg font-black font-sc text-[#0B2644]">
                        Recommended Reference Textbooks (5 Verified Texts with URLs)
                      </h4>
                    </div>
                    <span className="text-xs font-mono text-slate-500">
                      Live Hyperlinked External Catalogues
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(selectedCourse.generalReferences || []).map((r, rIdx) => (
                      <div key={rIdx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col justify-between space-y-3">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-0.5 rounded bg-[#0B2644] text-white font-mono font-bold text-[10px]">
                              Text #{r.id || rIdx + 1}
                            </span>
                            <span className="px-2 py-0.5 rounded bg-amber-100 text-[#7A470E] font-mono font-bold text-[10px] border border-amber-300">
                              ISBN: {r.isbn}
                            </span>
                          </div>
                          <h5 className="font-sc font-bold text-sm text-[#0B2644] leading-tight">
                            {r.title}
                          </h5>
                          <p className="text-xs text-slate-600 font-sans">
                            {r.author} ({r.year}) · <em>{r.edition}</em> · {r.publisher}
                          </p>
                          <p className="text-[11px] text-slate-500 font-sans pt-1">
                            {r.scope}
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-200">
                          {r.url && (
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-[#0B2644] hover:bg-[#133562] text-white text-[11px] font-bold font-sc transition-colors"
                            >
                              <span>Publisher Link</span>
                              <ExternalLink className="w-3 h-3 text-amber-300" />
                            </a>
                          )}
                          {r.altUrl && (
                            <a
                              href={r.altUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 text-[11px] font-bold font-sc transition-colors"
                            >
                              <span>WorldCat / Mirror</span>
                              <ExternalLink className="w-3 h-3 text-slate-500" />
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Econometric Software Laboratories */}
                <div className="border-t border-slate-200 pt-6">
                  <div className="mb-4">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#7A470E] font-sc block">
                      Computational Environments
                    </span>
                    <h4 className="text-lg font-black font-sc text-[#0B2644]">
                      Econometric & Statistical Software Suite
                    </h4>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { name: "Stata 18 (SE/MP)", category: "Primary Lab", url: "https://www.stata.com/", desc: "Primary econometric estimation engine for cross-sectional, 2SLS, and dynamic panel models." },
                      { name: "The R Project (4.3+)", category: "Open Source", url: "https://www.r-project.org/", desc: "Comprehensive statistical computing with plm, fixest, and ggplot2 diagnostic visualization." },
                      { name: "Python Statsmodels", category: "Data Science", url: "https://www.statsmodels.org/", desc: "High-performance Python libraries for econometric regression, GLM, and time-series ARIMA." },
                      { name: "Python Linearmodels", category: "Panel Econometrics", url: "https://bashtage.github.io/linearmodels/", desc: "Advanced panel estimators, IV/2SLS, and System GMM estimation." }
                    ].map((tool, tIdx) => (
                      <div key={tIdx} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col justify-between space-y-2">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <strong className="text-xs font-bold font-sc text-[#0B2644]">{tool.name}</strong>
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-[#7A470E] font-bold">{tool.category}</span>
                          </div>
                          <p className="text-[11px] text-slate-600">{tool.desc}</p>
                        </div>
                        <a
                          href={tool.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] font-bold text-[#0B2644] hover:text-[#7A470E] inline-flex items-center space-x-1 pt-1"
                        >
                          <span>Official Portal</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

            </div>
          ) : (
            /* Standard Course Overview for other courses */
            <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 sm:p-8 shadow-sm space-y-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#7A470E] font-sc block mb-1">
                  Executive Course Syllabus
                </span>
                <h3 className="text-xl font-black font-sc text-[#0B2644]">
                  Course Overview & Learning Outcomes
                </h3>
                <p className="mt-3 text-sm text-slate-700 leading-relaxed font-sans">
                  {selectedCourse.overview}
                </p>
              </div>

              <div className="border-t border-slate-200 pt-5">
                <h4 className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc mb-3">
                  Core Executive Learning Outcomes:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedCourse.learningOutcomes?.map((outcome, idx) => (
                    <div key={idx} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 flex items-start space-x-2.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span className="text-xs text-slate-800 font-sans leading-relaxed">{outcome}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modules / Lecture Sequence */}
              <div className="border-t border-slate-200 pt-5">
                <h4 className="text-xs font-black uppercase tracking-wider text-[#0B2644] font-sc mb-3">
                  Semester Lecture Outline:
                </h4>
                <div className="space-y-2.5">
                  {selectedCourse.modules?.map((m, idx) => (
                    <div key={idx} className="p-3.5 rounded-2xl bg-amber-50/50 border border-amber-200 flex items-center justify-between gap-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-xl bg-[#0B2644] text-amber-300 font-mono font-bold text-xs flex items-center justify-center shrink-0">
                          0{idx + 1}
                        </div>
                        <div>
                          <div className="text-xs font-bold text-[#0B2644] font-sc">{m.title}</div>
                          <div className="text-[11px] text-slate-600 font-sans">{m.keyTakeaway}</div>
                        </div>
                      </div>
                      <span className="text-[10px] font-mono font-bold text-slate-500 bg-white px-2 py-1 rounded-lg border border-slate-200 shrink-0">
                        {m.duration}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

        </div>
      )}

      {/* TAB 2: COURSE MATERIALS & CONTENT REPOSITORY */}
      {activeTab === 'materials' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                {selectedCourse.code} Materials Repository
              </span>
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Uploaded Syllabi, Lecture Slides, Case Studies & Datasets
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Manage, publish and distribute pedagogical resources to enrolled candidates.
              </p>
            </div>

            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2.5 bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc rounded-xl transition-all flex items-center space-x-1.5 shadow-md cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4 text-amber-300" />
              <span>Upload New Material</span>
            </button>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-slate-500 font-sc mr-1">Filter Type:</span>
            {['All', 'Syllabus', 'Slide Deck', 'Case', 'Video', 'Dataset'].map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedFilterCategory(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold font-sc transition-all cursor-pointer ${
                  selectedFilterCategory === cat
                    ? 'bg-[#0B2644] text-white shadow-xs'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Materials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMaterials.map((mat) => {
              const isSlide = mat.type.toLowerCase().includes('slide');
              const isVideo = mat.type.toLowerCase().includes('video');
              const isCase = mat.type.toLowerCase().includes('case');
              const isData = mat.type.toLowerCase().includes('data');

              return (
                <div key={mat.id || mat.title} className="bg-white p-5 rounded-3xl border-2 border-slate-200 shadow-sm flex flex-col justify-between space-y-4 hover:border-[#7A470E] transition-all">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className={`p-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 ${
                        isVideo ? 'bg-purple-50 text-purple-800 border border-purple-200' :
                        isSlide ? 'bg-amber-50 text-[#7A470E] border border-amber-200' :
                        isCase ? 'bg-blue-50 text-blue-800 border border-blue-200' :
                        isData ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                        'bg-slate-100 text-slate-800 border border-slate-200'
                      }`}>
                        {isVideo ? <Video className="w-3.5 h-3.5" /> :
                         isSlide ? <FileText className="w-3.5 h-3.5" /> :
                         isCase ? <BookOpen className="w-3.5 h-3.5" /> :
                         isData ? <Database className="w-3.5 h-3.5" /> :
                         <Download className="w-3.5 h-3.5" />}
                        <span className="font-sc">{mat.type}</span>
                      </div>
                      <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-bold">
                        {mat.status || 'Published'}
                      </span>
                    </div>

                    <div>
                      <h4 className="text-sm font-black font-sc text-[#0B2644] leading-snug">
                        {mat.title}
                      </h4>
                      <div className="text-[11px] text-slate-500 font-mono mt-1">
                        {mat.file || 'File Asset'} · {mat.size}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-xs">
                    <span className="text-[11px] text-slate-400 font-sans">{mat.date || 'Active'}</span>
                    <button
                      onClick={() => {
                        if (mat.type.toLowerCase().includes('ebook')) {
                          handleOpenEbook(selectedCourse.code);
                        } else {
                          setActionSuccessAlert(`Downloading ${mat.title} (${mat.size})`);
                        }
                      }}
                      className="px-3 py-1 rounded-lg bg-slate-50 hover:bg-[#0B2644] hover:text-white text-[#0B2644] font-bold font-sc transition-colors flex items-center space-x-1 cursor-pointer"
                    >
                      {mat.type.toLowerCase().includes('ebook') ? (
                        <>
                          <BookOpen className="w-3 h-3 text-amber-500" />
                          <span>Launch eBook</span>
                        </>
                      ) : (
                        <>
                          <Download className="w-3 h-3" />
                          <span>Download</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      )}

      {/* TAB 3: ASSIGNED ENROLLED STUDENTS & ROSTER */}
      {activeTab === 'students' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                {selectedCourse.code} Cohort
              </span>
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Enrolled Executive Scholars & Continuous Assessment Roster
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Scholars verified through Acceptance Fee, Bursary clearance, and course registration.
              </p>
            </div>

            <div className="flex items-center space-x-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200">
              <Users className="w-4 h-4 text-[#7A470E]" />
              <span>{selectedCourse.enrolledStudents?.length || 5} Verified Candidates</span>
            </div>
          </div>

          {/* Roster Table */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b-2 border-slate-200 text-[#0B2644] font-sc uppercase font-black tracking-wider text-[11px]">
                    <th className="p-4">Scholar Details</th>
                    <th className="p-4">Matriculation No</th>
                    <th className="p-4">Programme Track</th>
                    <th className="p-4">Attendance</th>
                    <th className="p-4">CA Submission</th>
                    <th className="p-4">Current Grade</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {selectedCourse.enrolledStudents?.map((student, idx) => (
                    <tr key={student.matric || idx} className="hover:bg-amber-50/40 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={student.avatar} 
                            alt={student.name} 
                            className="w-9 h-9 rounded-xl object-cover border border-slate-200 shrink-0" 
                          />
                          <div>
                            <div className="font-bold text-slate-900 font-sc text-xs">{student.name}</div>
                            <div className="text-[11px] text-slate-500">{student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 font-mono font-bold text-slate-700">{student.matric}</td>
                      <td className="p-4 font-sc font-bold text-[#0B2644]">{selectedCourse.programmeId?.toUpperCase()} Candidate</td>
                      <td className="p-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold font-mono bg-emerald-50 text-emerald-800 border border-emerald-200">
                          {student.attendanceRate}% Attended
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold font-mono ${
                          student.submissionStatus?.includes('Graded') ? 'bg-blue-50 text-blue-800 border border-blue-200' :
                          student.submissionStatus?.includes('Needs Review') ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                          'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        }`}>
                          {student.submissionStatus || 'Submitted'}
                        </span>
                      </td>
                      <td className="p-4 font-mono font-black text-xs text-[#0B2644]">
                        {student.grade || 'Pending'}
                      </td>
                      <td className="p-4 text-right">
                        <button
                          onClick={() => {
                            setActiveTab('messaging');
                            setReplyMessageText(`Dear Scholar ${student.name.split(' ')[0]},\n\nRe: Your performance and coursework in ${selectedCourse.code}...`);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-[#0B2644] text-white hover:bg-[#7A470E] font-bold font-sc text-[11px] transition-colors cursor-pointer"
                        >
                          Message Scholar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* TAB 4: ASSIGNMENTS & GRADING DESK */}
      {activeTab === 'assignments' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                {selectedCourse.code} Assessments
              </span>
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Continuous Assessment Deliverables & Grading Workbench
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Issue case briefs, review scholar dissertation drafts, and record official marks.
              </p>
            </div>

            <button
              onClick={() => setShowAssignmentModal(true)}
              className="px-4 py-2.5 bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc rounded-xl transition-all flex items-center space-x-1.5 shadow-md cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4 text-amber-300" />
              <span>Create New Assignment</span>
            </button>
          </div>

          {/* Active Assignments Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentCourseAssignments.map(asg => (
              <div key={asg.id} className="bg-white p-6 rounded-3xl border-2 border-slate-200 shadow-sm space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-bold font-mono text-[#7A470E] bg-amber-50 px-2.5 py-0.5 rounded-lg border border-amber-200">
                    {asg.weight}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    Due: {asg.dueDate}
                  </span>
                </div>

                <div>
                  <h4 className="text-base font-black font-sc text-[#0B2644]">
                    {asg.title}
                  </h4>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed font-sans">
                    {asg.brief}
                  </p>
                </div>

                <div className="border-t border-slate-100 pt-3 flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-mono text-[11px]">
                    Submissions: {currentCourseSubmissions.length} Received
                  </span>
                  <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-sc">
                    Rubric Active
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Submissions Pending Review Table */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 shadow-sm overflow-hidden space-y-4 p-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h4 className="text-base font-black font-sc text-[#0B2644]">
                Scholar Submission Queue ({currentCourseSubmissions.length} Candidates)
              </h4>
              <span className="text-xs font-bold font-mono text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg border border-amber-200">
                Continuous Assessment Desk
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[#0B2644] font-sc uppercase font-bold text-[11px]">
                    <th className="p-3">Candidate</th>
                    <th className="p-3">Task Title</th>
                    <th className="p-3">Submitted Date</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Grade</th>
                    <th className="p-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentCourseSubmissions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 font-bold text-slate-900 font-sc">
                        <div className="flex items-center space-x-2">
                          <img src={sub.avatar} alt="" className="w-7 h-7 rounded-lg object-cover" />
                          <div>
                            <div>{sub.studentName}</div>
                            <div className="text-[10px] text-slate-400 font-mono">{sub.matric}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 text-slate-700 max-w-xs truncate">{sub.taskTitle}</td>
                      <td className="p-3 text-slate-500 font-mono text-[11px]">{sub.submittedDate}</td>
                      <td className="p-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold font-mono ${
                          sub.status === 'Graded' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {sub.status}
                        </span>
                      </td>
                      <td className="p-3 font-mono font-bold text-xs text-[#0B2644]">
                        {sub.grade}
                      </td>
                      <td className="p-3 text-right">
                        <button
                          onClick={() => handleOpenGradeModal(sub)}
                          className="px-3 py-1.5 rounded-lg bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold font-sc text-[11px] transition-colors cursor-pointer shadow-xs"
                        >
                          {sub.status === 'Graded' ? 'Edit Grade' : 'Review & Grade'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* TAB 5: STUDENT MESSAGING DESK */}
      {activeTab === 'messaging' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                Executive Student Interaction Desk
              </span>
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                Faculty Direct Inquiries & Class Announcements
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Two-way academic consultation with enrolled scholars in {selectedCourse.code}.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleOpenBroadcastModal('urgent')}
                className="px-4 py-2 bg-gradient-to-r from-red-700 to-red-900 hover:from-red-800 hover:to-red-950 text-white text-xs font-bold font-sc rounded-xl transition-all shadow-md cursor-pointer flex items-center space-x-1.5 border border-red-400/50 animate-pulse"
                title="Broadcast to class and activate blinking red alert on student inbox"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
                <span>🚨 Broadcast with Blinking Inbox Alert</span>
              </button>

              <button
                onClick={() => handleOpenBroadcastModal('normal')}
                className="px-4 py-2 bg-gradient-to-r from-[#0B2644] to-[#06182B] hover:from-[#7A470E] hover:to-[#5C3306] text-white text-xs font-bold font-sc rounded-xl transition-all shadow-md cursor-pointer flex items-center space-x-1.5"
              >
                <Megaphone className="w-3.5 h-3.5 text-amber-300" />
                <span>Standard Broadcast</span>
              </button>
            </div>
          </div>

          {/* Class Broadcasts & Urgent Alerts Stream */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm space-y-4">
            <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Megaphone className="w-4 h-4 text-[#7A470E]" />
                <span className="text-xs font-black font-sc text-[#0B2644] uppercase tracking-wider">
                  Active Class Broadcasts for {selectedCourse.code} ({broadcastHistory.filter(b => b.courseCode === selectedCourse.code || !b.courseCode).length})
                </span>
              </div>
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded font-mono font-bold">
                ● Synchronized to Student Inboxes
              </span>
            </div>

            <div className="space-y-3">
              {broadcastHistory.filter(b => b.courseCode === selectedCourse.code || !b.courseCode).map(bcast => (
                <div 
                  key={bcast.id}
                  className={`p-4 rounded-2xl border ${
                    bcast.sendBlinkingAlert 
                      ? 'bg-red-50/80 border-red-200 ring-1 ring-red-300' 
                      : 'bg-amber-50/50 border-amber-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center space-x-2">
                      {bcast.sendBlinkingAlert && (
                        <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
                      )}
                      <h4 className="text-xs font-bold font-sc text-[#0B2644]">
                        {bcast.subject}
                      </h4>
                      {bcast.sendBlinkingAlert && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-600 text-white font-mono font-bold animate-pulse">
                          BLINKING RED ALERT ACTIVE
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono">
                      {bcast.timestamp}
                    </span>
                  </div>

                  <p className="text-xs text-slate-700 font-sans leading-relaxed whitespace-pre-line mb-3">
                    {bcast.message}
                  </p>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200/60 text-[11px] font-mono text-slate-500">
                    <div className="flex items-center space-x-2">
                      <span>Channels:</span>
                      {(bcast.channels || ['inbox']).map(ch => (
                        <span key={ch} className="px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 uppercase font-bold text-[9px]">
                          {ch}
                        </span>
                      ))}
                    </div>
                    <span className="text-emerald-700 font-bold">
                      ✓ Delivered to {selectedCourse.enrolledStudents?.length || 24} Enrolled Scholars
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conversation History Stream */}
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm space-y-4">
            <div className="border-b border-slate-100 pb-3 flex items-center justify-between">
              <span className="text-xs font-black font-sc text-[#0B2644] uppercase tracking-wider">
                Conversation Thread: {conversationHistory[0]?.subject || 'Academic Consultation'}
              </span>
              <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded font-mono font-bold">
                ● TLS Encrypted Desk
              </span>
            </div>

            <div className="space-y-4 max-h-[420px] overflow-y-auto p-1">
              {conversationHistory.map(msg => (
                <div 
                  key={msg.id}
                  className={`p-4 rounded-2xl border ${
                    msg.sender === 'faculty' 
                      ? 'bg-amber-50/70 border-amber-200 ml-4 sm:ml-12' 
                      : 'bg-slate-50 border-slate-200 mr-4 sm:mr-12'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-black font-sc text-[#0B2644]">{msg.senderName}</span>
                      <span className={`text-[9px] px-1.5 py-0.2 rounded font-mono font-bold ${
                        msg.sender === 'faculty' ? 'bg-[#7A470E] text-white' : 'bg-[#0B2644] text-white'
                      }`}>
                        {msg.sender === 'faculty' ? 'FACULTY' : 'SCHOLAR'}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono">{msg.time}</span>
                  </div>

                  <p className="text-xs text-slate-800 leading-relaxed font-sans whitespace-pre-line">
                    {msg.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Quick Reply Form */}
            <form onSubmit={handleSendReply} className="border-t border-slate-200 pt-4 space-y-3">
              <label className="block text-xs font-bold font-sc text-slate-700">
                Compose Faculty Reply to Candidate:
              </label>
              <textarea
                value={replyMessageText}
                onChange={(e) => setReplyMessageText(e.target.value)}
                placeholder="Type your official academic response or pedagogical guidance..."
                rows={3}
                className="w-full p-3 bg-slate-50 border border-slate-300 rounded-2xl text-xs text-slate-900 focus:outline-none focus:border-[#7A470E] font-sans"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 italic">
                  Messages are archived to student's academic record.
                </span>
                <button
                  type="submit"
                  disabled={!replyMessageText.trim()}
                  className="px-4 py-2 bg-[#7A470E] hover:bg-[#5C3306] disabled:opacity-40 text-white text-xs font-bold font-sc rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 shadow-sm"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Send Reply</span>
                </button>
              </div>
            </form>

          </div>

        </div>
      )}

      {/* TAB 6: RESEARCH & PUBLICATIONS DESK */}
      {activeTab === 'research' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          
          <div className="bg-white rounded-3xl border-2 border-slate-200 p-6 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block">
                Faculty Scholarship & Frontier Research
              </span>
              <h3 className="text-xl font-black font-sc text-[#0B2644]">
                {currentFaculty.name} Research Portfolio
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Active working papers, empirical research projects, grant funding, and journal submissions.
              </p>
            </div>

            <button
              onClick={() => setShowResearchModal(true)}
              className="px-4 py-2.5 bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc rounded-xl transition-all flex items-center space-x-1.5 shadow-md cursor-pointer shrink-0"
            >
              <Plus className="w-4 h-4 text-amber-300" />
              <span>Add Working Paper / Project</span>
            </button>
          </div>

          {/* Research Interests Tags */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 p-5 rounded-3xl border border-amber-200/80">
            <span className="text-xs font-black uppercase tracking-wider text-[#7A470E] font-sc block mb-2">
              Primary Academic Research Domains:
            </span>
            <div className="flex flex-wrap gap-2">
              {currentFaculty.researchInterests?.map((interest, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1.5 rounded-xl bg-white text-[#0B2644] border border-amber-300/80 text-xs font-bold font-sc shadow-2xs"
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>

          {/* Working Papers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentResearchPapers.map(paper => (
              <div key={paper.id} className="bg-white p-6 rounded-3xl border-2 border-slate-200 shadow-sm flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full border border-amber-200">
                      {paper.status}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">{paper.date}</span>
                  </div>

                  <h4 className="text-sm font-black font-sc text-[#0B2644] leading-snug">
                    {paper.title}
                  </h4>
                </div>

                <div className="border-t border-slate-100 pt-3 text-[11px] text-slate-600 font-sans flex items-center justify-between">
                  <span>Grant: {paper.grantSupport || 'Institutional Funding'}</span>
                  <button
                    onClick={() => setActionSuccessAlert(`Research paper "${paper.title}" dossier opened.`)}
                    className="text-[#7A470E] hover:underline font-bold font-sc cursor-pointer"
                  >
                    View Dossier →
                  </button>
                </div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* ========================================================================= */}
      {/* MODALS                                                                    */}
      {/* ========================================================================= */}

      {/* 1. UPLOAD MATERIAL MODAL */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setShowUploadModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-3">
                <span className="text-xs font-black uppercase text-[#7A470E] font-sc">
                  {selectedCourse.code} · Pedagogy Upload
                </span>
                <h3 className="text-lg font-black font-sc text-[#0B2644]">
                  Upload Course Learning Material
                </h3>
              </div>

              <form onSubmit={handleAddMaterial} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Material Title:
                  </label>
                  <input
                    type="text"
                    required
                    value={newMaterialTitle}
                    onChange={(e) => setNewMaterialTitle(e.target.value)}
                    placeholder="e.g., Module 2: Corporate Valuation Multiple Models (PDF)"
                    className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Material Type:
                    </label>
                    <select
                      value={newMaterialCategory}
                      onChange={(e) => setNewMaterialCategory(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    >
                      <option value="Slide Deck">Slide Deck (PDF/PPT)</option>
                      <option value="Case Study">Harvard / African Industry Case</option>
                      <option value="Video">Video Lecture Session (MP4)</option>
                      <option value="Dataset">Empirical Dataset (Excel/Stata)</option>
                      <option value="Syllabus">Syllabus & Course Reader</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Target Module:
                    </label>
                    <select
                      value={newMaterialTopic}
                      onChange={(e) => setNewMaterialTopic(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    >
                      <option value="Module 1">Module 1 (Foundations)</option>
                      <option value="Module 2">Module 2 (Advanced Methods)</option>
                      <option value="Module 3">Module 3 (Case Applications)</option>
                      <option value="Module 4">Module 4 (Empirical Analytics)</option>
                      <option value="Module 5">Module 5 (Doctoral Research)</option>
                    </select>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 text-[11px]">
                  Files uploaded will be hashed with SHA-256 and distributed via the high-speed AKSU Business School digital CDN.
                </div>

                <div className="pt-2 flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold font-sc rounded-xl shadow-md cursor-pointer"
                  >
                    Publish to Course
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 2. CREATE ASSIGNMENT MODAL */}
      {showAssignmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setShowAssignmentModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-3">
                <span className="text-xs font-black uppercase text-[#7A470E] font-sc">
                  {selectedCourse.code} · Assessment Creation
                </span>
                <h3 className="text-lg font-black font-sc text-[#0B2644]">
                  Issue New Coursework Assignment
                </h3>
              </div>

              <form onSubmit={handleAddAssignment} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Assignment Title:
                  </label>
                  <input
                    type="text"
                    required
                    value={newAssignTitle}
                    onChange={(e) => setNewAssignTitle(e.target.value)}
                    placeholder="e.g., Continuous Assessment 2: Empirical Model Estimation"
                    className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Task Brief & Instructions:
                  </label>
                  <textarea
                    rows={3}
                    required
                    value={newAssignBrief}
                    onChange={(e) => setNewAssignBrief(e.target.value)}
                    placeholder="Describe the problem dilemma, empirical requirements, and deliverables..."
                    className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Due Date:
                    </label>
                    <input
                      type="text"
                      value={newAssignDueDate}
                      onChange={(e) => setNewAssignDueDate(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Grade Weight:
                    </label>
                    <select
                      value={newAssignWeight}
                      onChange={(e) => setNewAssignWeight(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    >
                      <option value="20%">20% Continuous Assessment</option>
                      <option value="30%">30% Continuous Assessment</option>
                      <option value="40%">40% Major Term Project</option>
                      <option value="50%">50% Capstone Examination</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentModal(false)}
                    className="px-4 py-2 font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold font-sc rounded-xl shadow-md cursor-pointer"
                  >
                    Issue to Enrolled Scholars
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 3. REVIEW & GRADE SUBMISSION MODAL */}
      {showGradeModal && selectedSubmissionToGrade && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setShowGradeModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-3">
                <span className="text-xs font-black uppercase text-[#7A470E] font-sc">
                  Official Grade Recording
                </span>
                <h3 className="text-lg font-black font-sc text-[#0B2644]">
                  Evaluate {selectedSubmissionToGrade.studentName}
                </h3>
                <div className="text-xs text-slate-500 font-mono">
                  {selectedSubmissionToGrade.matric} · {selectedSubmissionToGrade.taskTitle}
                </div>
              </div>

              <form onSubmit={handleSaveGrade} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Continuous Assessment Mark (out of 100):
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={inputGradeScore}
                    onChange={(e) => setInputGradeScore(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-slate-300 font-mono text-base font-bold text-[#0B2644] focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Executive Academic Feedback:
                  </label>
                  <textarea
                    rows={4}
                    required
                    value={inputGradeFeedback}
                    onChange={(e) => setInputGradeFeedback(e.target.value)}
                    className="w-full p-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div className="pt-2 flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowGradeModal(false)}
                    className="px-4 py-2 font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-bold font-sc rounded-xl shadow-md cursor-pointer"
                  >
                    Record Grade & Notify Scholar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 4. ADD RESEARCH PAPER MODAL */}
      {showResearchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setShowResearchModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-4">
              <div className="border-b border-slate-100 pb-3">
                <span className="text-xs font-black uppercase text-[#7A470E] font-sc">
                  Faculty Research Portfolio
                </span>
                <h3 className="text-lg font-black font-sc text-[#0B2644]">
                  Register Working Paper or Grant Project
                </h3>
              </div>

              <form onSubmit={handleAddResearchPaper} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Research Manuscript / Project Title:
                  </label>
                  <input
                    type="text"
                    required
                    value={newResearchTitle}
                    onChange={(e) => setNewResearchTitle(e.target.value)}
                    placeholder="e.g., Cross-Sectional Asset Pricing in Frontier Markets..."
                    className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Publication Stage:
                    </label>
                    <select
                      value={newResearchStatus}
                      onChange={(e) => setNewResearchStatus(e.target.value)}
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    >
                      <option value="Under Review at ABS 3* Journal">Under Review at ABS 3* Journal</option>
                      <option value="Accepted for Publication">Accepted for Publication</option>
                      <option value="Working Paper / Data Collection">Working Paper / Data Collection</option>
                      <option value="Grant Proposal Submitted">Grant Proposal Submitted</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 font-sc mb-1">
                      Grant / Fellowship Support:
                    </label>
                    <input
                      type="text"
                      value={newResearchGrant}
                      onChange={(e) => setNewResearchGrant(e.target.value)}
                      placeholder="e.g., TETFUND National Research Fund"
                      className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E]"
                    />
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowResearchModal(false)}
                    className="px-4 py-2 font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-[#0B2644] hover:bg-[#7A470E] text-white font-bold font-sc rounded-xl shadow-md cursor-pointer"
                  >
                    Save to Academic Profile
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* CLASS BROADCAST & BLINKING RED ALERT MODAL */}
      {showBroadcastModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border-2 border-[#7A470E] relative text-slate-900 max-h-[92vh] overflow-y-auto">
            <button 
              onClick={() => setShowBroadcastModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-slate-700 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-5">
              
              {/* Header */}
              <div className="flex items-start space-x-3 border-b border-slate-100 pb-4">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-red-600 to-amber-700 text-white flex items-center justify-center shrink-0 shadow-md">
                  <Megaphone className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded-full border border-red-200">
                      Class Broadcast Console
                    </span>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs font-mono font-bold text-[#0B2644]">
                      {selectedCourse.code} ({selectedCourse.enrolledStudents?.length || 24} Scholars)
                    </span>
                  </div>
                  <h3 className="text-xl font-black font-sc text-[#0B2644] mt-0.5">
                    Broadcast Message to Enrolled Scholars
                  </h3>
                  <p className="text-xs text-slate-500">
                    Transmits an official academic advisory to all students enrolled in this course, with an optional high-priority blinking red inbox alert.
                  </p>
                </div>
              </div>

              {/* Template Quick Selectors */}
              <div>
                <label className="block text-xs font-bold font-sc text-slate-700 mb-1.5">
                  Load Template Advisory:
                </label>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setBroadcastSubject(`🚨 Urgent: Rescheduled Saturday Executive Seminar & Zoom Link — ${selectedCourse.code}`);
                      setBroadcastMessage(`Distinguished Scholars of ${selectedCourse.code},\n\nPlease take notice that our physical seminar session for this weekend has been synchronized with the hybrid digital amphitheatre. Video conference coordinates and supplementary slide decks are accessible in your Course Materials repository.\n\nAll enrolled scholars are required to confirm attendance and review the Saunders Onion reading before Saturday 09:00 AM prompt.\n\n— ${currentFaculty.name}, ${currentFaculty.rank}`);
                      setBroadcastPriority('urgent');
                      setSendBlinkingAlert(true);
                    }}
                    className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-800 text-[11px] font-bold border border-red-200 cursor-pointer transition-colors"
                  >
                    🚨 Urgent Seminar Reschedule
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBroadcastSubject(`Notice: Continuous Assessment 1 (CA1) Matrix Submission Deadline — ${selectedCourse.code}`);
                      setBroadcastMessage(`Dear Scholars,\n\nThe Continuous Assessment 1 submission window for ${selectedCourse.code} is officially open. Please submit your methodology chapter and empirical justification matrix via the portal.\n\nDeadline is strictly enforced at 11:59 PM next Friday.\n\n— ${currentFaculty.name}`);
                      setBroadcastPriority('urgent');
                      setSendBlinkingAlert(true);
                    }}
                    className="px-2.5 py-1 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-900 text-[11px] font-bold border border-amber-200 cursor-pointer transition-colors"
                  >
                    📌 CA1 Submission Deadline
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBroadcastSubject(`Supplementary Core Reading Material Uploaded — ${selectedCourse.code}`);
                      setBroadcastMessage(`Dear Enrolled Scholars,\n\nSupplementary HBR case notes and doctoral working papers have been added to your course repository. Please review ahead of our next module.\n\n— ${currentFaculty.name}`);
                      setBroadcastPriority('normal');
                      setSendBlinkingAlert(false);
                    }}
                    className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-900 text-[11px] font-bold border border-blue-200 cursor-pointer transition-colors"
                  >
                    📚 Supplementary Reading
                  </button>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSendBroadcast} className="space-y-4 text-xs">
                
                {/* Subject */}
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Broadcast Subject / Header:
                  </label>
                  <input
                    type="text"
                    required
                    value={broadcastSubject}
                    onChange={(e) => setBroadcastSubject(e.target.value)}
                    placeholder="e.g., Urgent: Continuous Assessment Submission & Live Video Link"
                    className="w-full p-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E] font-medium text-slate-900"
                  />
                </div>

                {/* Priority & Blinking Alert Feature Box */}
                <div className="p-3.5 rounded-2xl bg-gradient-to-r from-red-50 to-amber-50 border-2 border-red-300 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[#0B2644] font-sc text-xs flex items-center space-x-1.5">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <span>Attention & Notification Priority</span>
                    </span>
                    <span className="text-[10px] font-mono text-red-700 font-bold uppercase bg-white px-2 py-0.5 rounded border border-red-200">
                      Executive Directive
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Priority Level:</label>
                      <select
                        value={broadcastPriority}
                        onChange={(e) => {
                          setBroadcastPriority(e.target.value);
                          if (e.target.value === 'urgent') setSendBlinkingAlert(true);
                        }}
                        className="w-full p-2 rounded-xl border border-slate-300 bg-white font-medium"
                      >
                        <option value="urgent">🚨 Urgent (Immediate Attention Required)</option>
                        <option value="normal">Standard Academic Advisory</option>
                      </select>
                    </div>

                    <div className="flex items-center">
                      <label className="flex items-start space-x-2.5 cursor-pointer mt-3">
                        <input
                          type="checkbox"
                          checked={sendBlinkingAlert}
                          onChange={(e) => setSendBlinkingAlert(e.target.checked)}
                          className="mt-0.5 w-4 h-4 text-red-600 rounded border-slate-300 focus:ring-red-500 cursor-pointer"
                        />
                        <div>
                          <span className="font-black text-red-800 font-sc text-xs block leading-tight">
                            🚨 Trigger Blinking Red Inbox Alert in Student Console
                          </span>
                          <span className="text-[10px] text-slate-600 block mt-0.5">
                            Causes the student's top Inbox icon to continuously pulsate in vibrant red until opened and acknowledged.
                          </span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Live Visual Preview of the Blinking Red Inbox Beacon */}
                  {sendBlinkingAlert && (
                    <div className="p-3 rounded-xl bg-slate-900 text-white border border-slate-800 space-y-1.5 animate-in fade-in">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-mono text-amber-300 font-bold uppercase tracking-wider">
                          Live Visual Preview: Student Dashboard Header
                        </span>
                        <span className="text-[9px] font-mono text-emerald-400">Pulsing Active</span>
                      </div>
                      
                      <div className="flex items-center space-x-3 pt-1">
                        <span className="text-[11px] text-slate-400 font-sans">Student sees:</span>
                        <div className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white font-bold text-xs font-sc animate-pulse shadow-[0_0_18px_rgba(239,68,68,0.95)] border-2 border-red-300 ring-2 ring-red-400">
                          <Inbox className="w-3.5 h-3.5 text-white animate-bounce" />
                          <span>Inbox</span>
                          <span className="px-1.5 py-0.2 rounded-full bg-white text-red-700 font-mono text-[9px] font-black uppercase">
                            1 URGENT
                          </span>
                        </div>
                        <span className="text-[10px] text-amber-300 font-mono italic">
                          ← Blinking red alert waiting for scholar attention
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Delivery Channels */}
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Multi-Channel Dissemination:
                  </label>
                  <div className="flex flex-wrap gap-4 pt-1">
                    <label className="flex items-center space-x-1.5 cursor-pointer text-slate-700">
                      <input 
                        type="checkbox" 
                        checked={broadcastChannels.inbox} 
                        onChange={(e) => setBroadcastChannels(prev => ({ ...prev, inbox: e.target.checked }))}
                        className="rounded text-[#0B2644]" 
                      />
                      <span>In-LMS Scholar Inbox</span>
                    </label>
                    <label className="flex items-center space-x-1.5 cursor-pointer text-slate-700">
                      <input 
                        type="checkbox" 
                        checked={broadcastChannels.whatsapp} 
                        onChange={(e) => setBroadcastChannels(prev => ({ ...prev, whatsapp: e.target.checked }))}
                        className="rounded text-emerald-600" 
                      />
                      <span>Official Class WhatsApp Syndicate</span>
                    </label>
                    <label className="flex items-center space-x-1.5 cursor-pointer text-slate-700">
                      <input 
                        type="checkbox" 
                        checked={broadcastChannels.email} 
                        onChange={(e) => setBroadcastChannels(prev => ({ ...prev, email: e.target.checked }))}
                        className="rounded text-blue-600" 
                      />
                      <span>Email & SMS Dispatch</span>
                    </label>
                  </div>
                </div>

                {/* Message Body */}
                <div>
                  <label className="block font-bold text-slate-700 font-sc mb-1">
                    Broadcast Message Body:
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="Type official directive, instructions, seminar venue, or video link..."
                    className="w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:border-[#7A470E] font-sans text-slate-900 leading-relaxed"
                  />
                </div>

                {/* Action Buttons */}
                <div className="pt-2 flex items-center justify-end space-x-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setShowBroadcastModal(false)}
                    className="px-4 py-2 font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 bg-gradient-to-r from-red-700 to-[#0B2644] hover:from-red-800 hover:to-[#06182B] text-white font-bold font-sc rounded-xl shadow-md cursor-pointer flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4 text-amber-300" />
                    <span>Disseminate Broadcast & Trigger Alert</span>
                  </button>
                </div>

              </form>

            </div>
          </div>
        </div>
      )}

      {/* 5. HYBRID BROADCAST STUDIO MODAL */}
      {showLiveStudioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-[#030F1C] text-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl border-4 border-[#7A470E] relative">
            <button 
              onClick={() => setShowLiveStudioModal(false)}
              className="absolute top-5 right-5 p-1 rounded-full text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-5">
              <div className="flex items-center space-x-3 border-b border-slate-800 pb-4">
                <div className="w-10 h-10 rounded-2xl bg-red-600/20 text-red-400 flex items-center justify-center shrink-0 border border-red-500/40">
                  <Video className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <span className="text-[11px] font-mono text-red-400 font-bold uppercase">
                    Live Broadcast Stream · Hybrid Digital Amphitheatre
                  </span>
                  <h3 className="text-xl font-black font-sc text-white">
                    {selectedCourse.code}: {selectedCourse.title}
                  </h3>
                </div>
              </div>

              {/* Video Preview Canvas */}
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-900 border-2 border-slate-800 flex items-center justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1200&auto=format&fit=crop" 
                  alt="Studio Stage"
                  className="w-full h-full object-cover opacity-35"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-3 p-4 text-center z-10">
                  <div className="w-16 h-16 rounded-full bg-[#7A470E]/80 border-2 border-amber-300 text-white flex items-center justify-center shadow-2xl">
                    <Video className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="font-sc font-black text-lg text-white">
                      Amphitheatre Hybrid Broadcasting Live
                    </h4>
                    <p className="text-xs text-slate-300 font-sans mt-0.5">
                      Transmitting 1080p Ultra-HD to {selectedCourse.enrolledStudents?.length || 5} Verified Scholars Across Nigeria
                    </p>
                  </div>
                  <div className="inline-flex items-center space-x-2 text-[11px] font-mono bg-black/60 px-3 py-1 rounded-full text-emerald-400 border border-emerald-500/40">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>Host: {currentFaculty.formalSalutation}</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 text-xs">
                <span className="text-slate-400 font-mono text-[11px]">
                  Venue: {selectedCourse.venue}
                </span>
                <button
                  onClick={() => setShowLiveStudioModal(false)}
                  className="px-5 py-2.5 bg-[#7A470E] hover:bg-[#5C3306] text-white font-bold font-sc rounded-xl cursor-pointer"
                >
                  Close Studio Preview
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Interactive Course eBook Reader Modal */}
      <CourseEbookModal 
        isOpen={showEbookModal}
        onClose={() => setShowEbookModal(false)}
        courseCode={ebookCourseCode}
      />

    </div>
  );
};
